import React from "react";
import { playToggleOnSound, playToggleOffSound, playCloseSound, playWarningSound, playSliderSound } from "../../Sounds";

export default function Settings({ theme, soundEnabled, volume, onThemeToggle, onSoundToggle, onVolumeChange, musicOn, musicVolume, onMusicToggle, onMusicVolumeChange, cutscenes, onCutsceneToggle, wheelEnabled, onWheelToggle, authUser, onSignIn, onSignOut, onClose }) {
  let sliderTimer = null;
  const throttledSlider = (cb, val) => {
    if (sliderTimer) return;
    playSliderSound();
    cb(val);
    sliderTimer = setTimeout(() => { sliderTimer = null; }, 60);
  };
  return (
    <div className="modal-overlay" onClick={() => { playCloseSound(); onClose(); }}>
      <div className="modal-box settings-box" onClick={e => e.stopPropagation()}>

        <h2 style={{ marginBottom: 4, textAlign: "center" }}>⚙️ Settings</h2>
        <p className="settings-subtitle">Customise your experience</p>

        {/* ── Appearance ── */}
        <div className="settings-section">
          <div className="settings-section-title">Appearance</div>

          <div className="settings-row">
            <div className="settings-row-left">
              <div className="settings-row-emoji">{theme === "light" ? "🌙" : "☀️"}</div>
              <div className="settings-row-info">
                <div className="settings-row-name">{theme === "light" ? "Dark Mode" : "Light Mode"}</div>
                <div className="settings-row-desc">Switch to {theme === "light" ? "dark" : "light"} theme</div>
              </div>
            </div>
            <label className="settings-toggle">
              <input type="checkbox" checked={theme === "dark"} onChange={e => { e.target.checked ? playToggleOnSound() : playToggleOffSound(); onThemeToggle(); }} />
              <span className="settings-toggle-track" />
            </label>
          </div>
        </div>

        {/* ── Audio ── */}
        <div className="settings-section">
          <div className="settings-section-title">Audio</div>

          <div className="settings-row">
            <div className="settings-row-left">
              <div className="settings-row-emoji">{soundEnabled ? "🔊" : "🔇"}</div>
              <div className="settings-row-info">
                <div className="settings-row-name">Sound Effects</div>
                <div className="settings-row-desc">Roll sounds, rarity fanfares & coin chimes</div>
              </div>
            </div>
            <label className="settings-toggle">
              <input type="checkbox" checked={soundEnabled} onChange={e => { e.target.checked ? playToggleOnSound() : playToggleOffSound(); onSoundToggle(); }} />
              <span className="settings-toggle-track" />
            </label>
          </div>

          {soundEnabled && (
            <div className="settings-slider-row">
              <span className="settings-slider-label">🔈</span>
              <input
                type="range" min="0" max="100" value={volume}
                onChange={e => throttledSlider(onVolumeChange, Number(e.target.value))}
                className="settings-slider"
              />
              <span className="settings-slider-label">{volume}%</span>
            </div>
          )}

          <div className="settings-row">
            <div className="settings-row-left">
              <div className="settings-row-emoji">🎵</div>
              <div className="settings-row-info">
                <div className="settings-row-name">Background Music</div>
                <div className="settings-row-desc">Looping ambient music while you play</div>
              </div>
            </div>
            <label className="settings-toggle">
              <input type="checkbox" checked={!!musicOn} onChange={e => { e.target.checked ? playToggleOnSound() : playToggleOffSound(); onMusicToggle(); }} />
              <span className="settings-toggle-track" />
            </label>
          </div>

          {musicOn && (
            <div className="settings-slider-row">
              <span className="settings-slider-label">🎵</span>
              <input
                type="range" min="0" max="100" value={musicVolume ?? 40}
                onChange={e => throttledSlider(onMusicVolumeChange, Number(e.target.value))}
                className="settings-slider"
              />
              <span className="settings-slider-label">{musicVolume ?? 40}%</span>
            </div>
          )}
        </div>

        {/* ── Cutscenes ── */}
        <div className="settings-section">
          <div className="settings-section-title">Cutscenes</div>
          {[
            { id: "Mythic", label: "Mythic Reveal", desc: "Animated reveal when you pull a Mythic" },
            { id: "Ultra",  label: "Ultra Reveal",  desc: "Animated reveal when you pull an Ultra"  },
          ].map(({ id, label, desc }) => (
            <div className="settings-row" key={id}>
              <div className="settings-row-left">
                <div className="settings-row-emoji">🎬</div>
                <div className="settings-row-info">
                  <div className="settings-row-name">{label}</div>
                  <div className="settings-row-desc">{desc}</div>
                </div>
              </div>
              <label className="settings-toggle">
                <input type="checkbox" checked={cutscenes?.[id] !== false} onChange={e => { e.target.checked ? playToggleOnSound() : playToggleOffSound(); onCutsceneToggle(id); }} />
                <span className="settings-toggle-track" />
              </label>
            </div>
          ))}
        </div>

        {/* ── Spin Wheel ── */}
        <div className="settings-section">
          <div className="settings-section-title">Roll Style</div>
          <div className="settings-row">
            <div className="settings-row-left">
              <div className="settings-row-emoji">🎡</div>
              <div className="settings-row-info">
                <div className="settings-row-name">Spin Wheel</div>
                <div className="settings-row-desc">Show a spin wheel before each card reveal</div>
              </div>
            </div>
            <label className="settings-toggle">
              <input type="checkbox" checked={!!wheelEnabled} onChange={e => { e.target.checked ? playToggleOnSound() : playToggleOffSound(); onWheelToggle(); }} />
              <span className="settings-toggle-track" />
            </label>
          </div>
        </div>

        {/* ── Account ── */}
        <div className="settings-section">
          <div className="settings-section-title">Account</div>
          {authUser ? (
            <div className="settings-row">
              <div className="settings-row-left">
                {authUser.photoURL
                  ? <img src={authUser.photoURL} alt="avatar" className="settings-avatar-img" referrerPolicy="no-referrer" />
                  : <div className="settings-row-emoji">👤</div>
                }
                <div className="settings-row-info">
                  <div className="settings-row-name">{authUser.displayName || "Signed in"}</div>
                  <div className="settings-row-desc">{authUser.email} · ☁️ Progress saved</div>
                </div>
              </div>
              <button
                style={{ background: "var(--bg-card)", color: "var(--text-muted)", border: "1px solid var(--border)", borderRadius: 8, padding: "8px 14px", fontWeight: 700, cursor: "pointer", fontSize: ".8rem", whiteSpace: "nowrap" }}
                onClick={onSignOut}
              >
                Sign out
              </button>
            </div>
          ) : (
            <div className="settings-row">
              <div className="settings-row-left">
                <div className="settings-row-emoji">☁️</div>
                <div className="settings-row-info">
                  <div className="settings-row-name">Save your progress</div>
                  <div className="settings-row-desc">Sign in with Google to back up your collection</div>
                </div>
              </div>
              <button
                style={{ background: "var(--neon-purple)", color: "#fff", border: "none", borderRadius: 8, padding: "8px 14px", fontWeight: 700, cursor: "pointer", fontSize: ".8rem", whiteSpace: "nowrap" }}
                onClick={onSignIn}
              >
                Sign in
              </button>
            </div>
          )}
        </div>

        {/* ── Danger Zone ── */}
        <div className="settings-section">
          <div className="settings-section-title" style={{color:"#ff4444"}}>Danger Zone</div>
          <div className="settings-row">
            <div className="settings-row-left">
              <div className="settings-row-emoji">🗑️</div>
              <div className="settings-row-info">
                <div className="settings-row-name">Reset All Progress</div>
                <div className="settings-row-desc">Wipes your collection, coins & rolls — cannot be undone</div>
              </div>
            </div>
            <button
              style={{background:"#ff4444",color:"#fff",border:"none",borderRadius:8,padding:"8px 14px",fontWeight:800,cursor:"pointer",fontSize:".8rem",whiteSpace:"nowrap"}}
              onClick={() => {
                playWarningSound();
                if (window.confirm("Are you sure? This will delete ALL your progress permanently.")) {
                  localStorage.clear();
                  window.location.reload();
                }
              }}
            >
              Reset
            </button>
          </div>
        </div>

        <div className="settings-divider" />
        <div className="settings-version">Hamster Gambler • v2.0</div>
        <button className="back-button" onClick={() => { playCloseSound(); onClose(); }} style={{ marginTop: 20, width: "100%" }}>
          Close
        </button>
      </div>
    </div>
  );
}